import React from 'react';
import { Ship, FileText, Clock, MapPin, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const EXPORT_BG = 'https://images.unsplash.com/photo-1508404999913-79a3a2e75437?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxzaGlwcGluZyUyMGNvbnRhaW5lciUyMHBvcnQlMjBuaWdodCUyMGFlcmlhbHxlbnwwfHx8fDE3NzI0NzY4NDB8MA&ixlib=rb-4.1.0&q=85';

export const ExportPage = () => {
  const { t } = useApp();

  const processSteps = t('export.process.steps') || [];
  const docItems = t('export.docs.items') || [];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="export-page">
      {/* Hero */}
      <section 
        className="py-32 relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.8), rgba(5,5,5,0.95)), url(${EXPORT_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-6">
            <Ship className="w-4 h-4" />
            Ocean Freight
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="export-title">
            {t('export.title')}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-8">
            {t('export.subtitle')}
          </p>
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 px-6 py-3 bg-[#00D4FF]/10 border border-[#00D4FF]/30">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D4FF]" />
              <span className="text-white font-medium">{t('export.timeline').split('|')[0].trim()}</span>
            </div>
            <span className="hidden sm:inline text-white/30">|</span>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D4FF]" />
              <span className="text-white font-medium">{t('export.timeline').split('|')[1]?.trim()}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Miami Hub */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass p-8 flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
              <MapPin className="w-10 h-10" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold mb-2">{t('exportPage.miamiExportHub')}</h2>
              <p className="text-white/60">
                All shipments are consolidated and exported from our Miami facility, providing efficient 
                access to all major Latin American ports with competitive freight rates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Process Steps */}
            <div>
              <h2 className="text-3xl font-bold mb-8">{t('export.process.title')}</h2>
              <div className="space-y-6">
                {Array.isArray(processSteps) && processSteps.map((step, idx) => (
                  <div 
                    key={idx}
                    className="flex gap-4"
                    data-testid={`process-step-${idx + 1}`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#00D4FF] text-black font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 pt-2">
                      <p className="text-white/80">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documentation */}
            <div>
              <h2 className="text-3xl font-bold mb-8">{t('export.docs.title')}</h2>
              <div className="card-dark p-8">
                <ul className="space-y-4">
                  {Array.isArray(docItems) && docItems.map((doc, idx) => (
                    <li 
                      key={idx}
                      className="flex items-center gap-3"
                      data-testid={`doc-item-${idx + 1}`}
                    >
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      <span className="text-white/80">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Forwarder Responsibilities */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">Freight Forwarder Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              'Ocean Booking',
              'Export Filing',
              'Insurance',
              'Customs Clearance',
              'Port Handling',
              'Final Delivery',
            ].map((service) => (
              <div 
                key={service}
                className="card-dark p-4 text-center"
              >
                <p className="text-white/70 text-sm">{service}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('exportPage.servingLatinAmerica')}</h2>
          <p className="text-white/60 mb-12 max-w-2xl mx-auto">
            {t('exportPage.servingSubtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              'Mexico', 'Colombia', 'Peru', 'Chile', 'Argentina', 'Brazil', 
              'Ecuador', 'Venezuela', 'Panama', 'Costa Rica', 'Guatemala', 'Dominican Republic'
            ].map((country) => (
              <span 
                key={country}
                className="px-4 py-2 bg-[#0A0A0A] border border-white/10 text-white/70 text-sm"
              >
                {country}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
